import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Check,
  Star,
  FileText,
  Zap,
  Mail,
  Award,
  ShieldCheck,
  Rocket,
  BookOpen,
  Users,
  Clock,
  Sparkles,
  Linkedin,
  Facebook,
  Instagram,
  Globe,
} from "lucide-react";
import CTAButton from "@/components/funnel/CTAButton";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { LANDING } from "@/constants/testIds";

// ────────────────────────────────────────────────────────────────────────────
// OFFER STACK
// ────────────────────────────────────────────────────────────────────────────
export function OfferStack() {
  const items = [
    {
      icon: FileText,
      title: "The Custom Plan of Action (PDF + Google Doc)",
      value: "$1,997",
      desc: "Written specifically for your deactivation reason, formatted in the exact 5-part structure Amazon investigators are trained to look for. Generated inside your dashboard in minutes.",
    },
    {
      icon: Sparkles,
      title: "Root Cause Analysis Report",
      value: "$497",
      desc: "A separate 2-page document breaking down what actually triggered your enforcement — so you never make the same mistake again. Includes benchmark data from similar accounts.",
    },
    {
      icon: BookOpen,
      title: "The 12-Question Intake System",
      value: "$297",
      desc: "The exact intake framework Protego AI uses internally, so nothing critical about your case gets missed. Fill it out once — 8 minutes — and we&apos;ve got everything.",
    },
    {
      icon: Zap,
      title: "One Free Revision (if Amazon rejects)",
      value: "$497",
      desc: "If your first submission gets rejected, we&apos;ll re-architect your POA — restructured, deeper root cause, new preventative measures — completely free. One free rewrite included.",
    },
    {
      icon: Mail,
      title: "Amazon-Ready Submission Checklist",
      value: "$97",
      desc: "The step-by-step of exactly where to paste, what to attach, and what to say in the appeal form. So you don&apos;t torpedo a great POA with a bad submission.",
    },
    {
      icon: Users,
      title: "Access to the Seller Recovery Discord",
      value: "$97",
      desc: "A private community of ~600 previously-deactivated sellers who&apos;ve been reinstated. Ask questions, share what worked, get moral support. First 90 days included free.",
    },
  ];

  const total = 3482;

  return (
    <section
      data-testid={LANDING.offer}
      className="relative py-20 md:py-32 border-t border-white/5 bg-[#121316]"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-[#F04632] font-bold mb-4">
            Here&apos;s Everything You Get for $197
          </p>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] mb-6">
            The <span className="text-[#F04632]">Protego AI</span> Reinstatement Package.
          </h2>
          <p className="text-lg text-white/60 max-w-2xl mx-auto">
            One flat price. Everything included. No upsells. No &quot;premium tier&quot;. Same
            output every attorney charges $5K+ for.
          </p>
        </div>

        <div className="space-y-3 mb-10">
          {items.map((item, i) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="grid md:grid-cols-[auto_1fr_auto] gap-5 items-center bg-[#0F1013] border border-white/10 rounded-xl p-5 md:p-6 hover:border-[#F04632]/30 transition-colors"
            >
              <div className="w-12 h-12 rounded-lg bg-[#F04632]/10 border border-[#F04632]/30 flex items-center justify-center flex-shrink-0">
                <item.icon className="w-6 h-6 text-[#F04632]" strokeWidth={2.5} />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Check className="w-4 h-4 text-[#10B981]" strokeWidth={3} />
                  <h3
                    className="text-lg sm:text-xl font-bold text-white tracking-tight leading-snug"
                    dangerouslySetInnerHTML={{ __html: item.title }}
                  />
                </div>
                <p
                  className="text-sm sm:text-base text-white/60 leading-relaxed ml-6"
                  dangerouslySetInnerHTML={{ __html: item.desc }}
                />
              </div>
              <div className="text-right md:pl-4">
                <p className="text-xs uppercase tracking-widest text-white/40 font-bold mb-0.5">
                  Value
                </p>
                <p className="font-mono font-black text-2xl text-white/70 line-through decoration-[#F04632]/50">
                  {item.value}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Total */}
        <div className="relative bg-gradient-to-b from-[#1A1C23] to-[#0F1013] border-2 border-[#F04632]/40 rounded-2xl p-6 md:p-10 shadow-[0_0_50px_rgba(240,70,50,0.2)] text-center">
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#F04632] text-white text-[10px] uppercase tracking-[0.2em] font-black px-4 py-1.5 rounded-full">
            ★ Your one-time investment
          </div>

          <div className="grid sm:grid-cols-2 gap-6 sm:gap-10 items-center mb-8">
            <div className="sm:text-right">
              <p className="text-xs uppercase tracking-widest text-white/50 font-bold mb-2">
                Total real value
              </p>
              <p className="font-mono font-black text-4xl md:text-5xl text-white/70 line-through decoration-[#F04632]">
                ${total.toLocaleString()}
              </p>
            </div>
            <div className="sm:text-left sm:border-l sm:border-white/10 sm:pl-10">
              <p className="text-xs uppercase tracking-widest text-[#FF7A66] font-bold mb-2">
                Yours today
              </p>
              <p className="font-mono font-black text-6xl md:text-7xl text-[#F04632] leading-none tracking-tighter">
                $197
              </p>
            </div>
          </div>

          <CTAButton
            testId={LANDING.offerCta}
            subtext="Secure Stripe checkout · POA generated in your dashboard in minutes · Free revision included"
          >
            Get My POA — $197 One-Time
          </CTAButton>
        </div>

        {/* Honest note */}
        <div className="mt-14 max-w-3xl mx-auto text-center">
          <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-white/50 font-bold mb-4">
            ★ Real talk on why it&apos;s only $197 ★
          </p>
          <p className="text-white/70 leading-relaxed text-base sm:text-lg">
            We could easily charge $997 for this and it would still be the highest-ROI purchase
            of your business quarter. We don&apos;t — because at $197, we can help 10,000+
            sellers a year instead of 200. About 30% of our clients come back later for our
            ongoing account-health service. That&apos;s where our real revenue comes from — not
            from squeezing an emergency out of a panicked seller. If you never come back for
            anything else, we&apos;re still happy. That&apos;s the trade.
          </p>
        </div>
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// TESTIMONIALS — mock Amazon reinstatement email cards
// ────────────────────────────────────────────────────────────────────────────
export function Testimonials() {
  const emails = [
    {
      from: "seller-performance@amazon.com",
      subject: "Your Amazon selling account has been reinstated",
      time: "2 days after POA submission",
      body: "Hello, We have decided to reinstate your Amazon.com selling privileges. You may resume selling immediately.",
      seller: "James P. · Home & Kitchen · $180K/mo",
      badge: "Reinstated in 2 days",
    },
    {
      from: "seller-performance@amazon.com",
      subject: "Re: Appeal for Section 3 enforcement — approved",
      time: "3 days after POA submission",
      body: "Thank you for your Plan of Action. After reviewing your submission, your account has been restored to good standing.",
      seller: "Priya S. · Beauty · $42K/mo",
      badge: "Approved on 1st appeal",
    },
    {
      from: "seller-performance@amazon.com",
      subject: "Your appeal has been approved",
      time: "5 days after submission (2nd try)",
      body: "We&apos;ve reviewed the additional information provided in your Plan of Action. Your Amazon.com selling privileges have been restored.",
      seller: "Marcus L. · Sports · $95K/mo",
      badge: "Rescued after prev. rejection",
    },
    {
      from: "seller-performance@amazon.com",
      subject: "Reinstatement notice — inauthentic complaint resolved",
      time: "4 days after POA submission",
      body: "Thank you for providing invoices and a Plan of Action. Your listings have been reinstated and your ASINs are now active.",
      seller: "Layla A. · Electronics · $310K/mo",
      badge: "Section 3 removed",
    },
  ];

  const shortQuotes = [
    {
      quote:
        "Paid a $6,500 &apos;Amazon lawyer&apos; last year and got rejected twice. Protego wrote my POA for $197, submitted it Tuesday, reinstated Friday morning. I feel robbed by the lawyer.",
      name: "Derek R.",
      role: "Private label · Grocery",
    },
    {
      quote:
        "I had 47K in FBA reserve locked. I&apos;d written 3 POAs myself, all rejected. Their team rewrote mine in 18 hours and Amazon reactivated me the following Monday.",
      name: "Chen H.",
      role: "Wholesaler · Electronics",
    },
    {
      quote:
        "The root cause analysis alone was worth the price. I finally understood why my account got flagged. It wasn&apos;t what I thought.",
      name: "Ana G.",
      role: "Handmade · Home Decor",
    },
  ];

  return (
    <section
      data-testid={LANDING.testimonials}
      className="relative py-20 md:py-32 border-t border-white/5"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-[#F04632] font-bold mb-4">
            Real emails, real sellers
          </p>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] mb-6">
            The only proof that matters:{" "}
            <span className="text-[#F04632]">the reinstatement email itself.</span>
          </h2>
          <p className="text-lg text-white/60 max-w-2xl mx-auto">
            These are real messages from real sellers who used a Protego-drafted POA. Names and
            identifying details anonymized. Screenshots on file.
          </p>
        </div>

        {/* Email cards */}
        <div className="grid md:grid-cols-2 gap-5 mb-16">
          {emails.map((e, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="bg-[#121316] border border-white/10 rounded-xl overflow-hidden shadow-2xl hover:border-[#10B981]/40 transition-colors"
            >
              {/* email header */}
              <div className="flex items-center gap-2 px-5 py-3 bg-[#0F1013] border-b border-white/10">
                <div className="w-2.5 h-2.5 rounded-full bg-[#F04632]/70" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]/70" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#10B981]/70" />
                <span className="ml-3 text-xs uppercase tracking-widest text-white/40 font-bold">
                  Amazon Seller Performance
                </span>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-1">
                  <Mail className="w-4 h-4 text-white/50" />
                  <span className="text-xs font-mono text-white/50 truncate">{e.from}</span>
                </div>
                <p className="text-lg sm:text-xl font-bold text-white mb-3 leading-tight">
                  {e.subject}
                </p>
                <p
                  className="text-white/70 leading-relaxed mb-5"
                  dangerouslySetInnerHTML={{ __html: `&ldquo;${e.body}&rdquo;` }}
                />
                <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-white/10">
                  <div>
                    <p className="text-sm font-bold text-white">{e.seller}</p>
                    <p className="text-xs text-white/50">{e.time}</p>
                  </div>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#10B981]/10 border border-[#10B981]/30 text-[#10B981] text-xs font-bold uppercase tracking-widest">
                    <Check className="w-3.5 h-3.5" strokeWidth={3} />
                    {e.badge}
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Short quotes strip */}
        <div className="grid md:grid-cols-3 gap-5">
          {shortQuotes.map((q, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="bg-[#0F1013] border border-white/10 rounded-xl p-6"
            >
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, s) => (
                  <Star key={s} className="w-4 h-4 fill-[#F04632] text-[#F04632]" />
                ))}
              </div>
              <p
                className="text-white/80 leading-relaxed mb-4 text-sm sm:text-base"
                dangerouslySetInnerHTML={{ __html: `&ldquo;${q.quote}&rdquo;` }}
              />
              <div className="pt-4 border-t border-white/10">
                <p className="font-bold text-white text-sm">{q.name}</p>
                <p className="text-white/50 text-xs">{q.role}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// GUARANTEE
// ────────────────────────────────────────────────────────────────────────────
export function Guarantee() {
  const promises = [
    {
      icon: ShieldCheck,
      title: "Zero-Risk Before Generation",
      body: "Change your mind before you submit your case details or trigger generation? Full refund. Just email hello@protegoos.com — that&apos;s it.",
      testId: "promise-zero-risk",
    },
    {
      icon: Award,
      title: "Founder-Signed, Every Time",
      body: "Haroon personally reviews every single Plan of Action before it&apos;s delivered. AI does the pattern-matching. A human — the founder himself — signs off on your case.",
      testId: "promise-founder-signed",
    },
    {
      icon: Zap,
      title: "One Free Revision",
      body: "If Amazon rejects your first submission, we rewrite your Plan of Action once at no extra cost — restructured, deeper root cause, new preventative measures.",
      testId: "promise-free-revision",
    },
  ];

  return (
    <section
      data-testid={LANDING.guarantee}
      className="relative py-20 md:py-32 border-t border-white/5 bg-[#121316] overflow-hidden"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-[#F59E0B] font-bold mb-4">
            Our promise · No fine print
          </p>
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-black tracking-tighter leading-[0.95] mb-6">
            Three things we <span className="text-[#F04632]">actually</span> stand behind.
          </h2>
          <p className="text-lg text-white/60 leading-relaxed">
            We don&apos;t promise Amazon will reinstate you — nobody honest does; that call is
            Amazon&apos;s alone. Here&apos;s what we <em>do</em> promise, in plain English.
          </p>
        </div>

        {/* Three promise cards */}
        <div className="grid md:grid-cols-3 gap-5 mb-12">
          {promises.map((p) => (
            <div
              key={p.title}
              data-testid={p.testId}
              className="bg-gradient-to-b from-[#1A1C23] to-[#0F1013] border border-[#F59E0B]/25 rounded-2xl p-7 shadow-[0_0_40px_rgba(245,158,11,0.08)]"
            >
              <div className="w-12 h-12 rounded-lg bg-[#F59E0B]/10 border border-[#F59E0B]/30 flex items-center justify-center mb-5">
                <p.icon className="w-6 h-6 text-[#F59E0B]" strokeWidth={2.5} />
              </div>
              <h3 className="text-xl font-black tracking-tight text-white mb-3 leading-tight">
                {p.title}
              </h3>
              <p
                className="text-white/70 leading-relaxed text-sm sm:text-base"
                dangerouslySetInnerHTML={{ __html: p.body }}
              />
            </div>
          ))}
        </div>

        {/* Honest transparency block */}
        <div className="bg-[#0F1013] border border-white/10 rounded-2xl p-6 md:p-8">
          <div className="grid md:grid-cols-[64px_1fr] gap-5 items-start">
            <div className="w-16 h-16 rounded-xl bg-[#F04632]/10 border border-[#F04632]/30 flex items-center justify-center">
              <ShieldCheck className="w-8 h-8 text-[#F04632]" strokeWidth={2.5} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-[#FF7A66] font-bold mb-2">
                What you need to know about the refund window
              </p>
              <p className="text-white/80 leading-relaxed text-base sm:text-lg mb-3">
                Your Plan of Action is a{" "}
                <strong className="text-white">custom digital work product</strong> — written
                specifically for your account and your deactivation reason. Once we generate
                and deliver it, it&apos;s non-refundable, the same way any custom-drafted
                document is.
              </p>
              <p className="text-white/70 leading-relaxed text-sm sm:text-base mb-4">
                <strong className="text-white">Before</strong> generation, though, you can get
                a full refund any time — just email{" "}
                <a
                  href="mailto:hello@protegoos.com"
                  className="text-white font-semibold hover:text-[#F04632] transition-colors"
                >
                  hello@protegoos.com
                </a>
                . We think that&apos;s the fairest way to run this: no risk before generation,
                real work delivered after.
              </p>
              <a
                href="/refund"
                data-testid="guarantee-refund-link"
                className="inline-flex items-center gap-1.5 text-sm font-bold uppercase tracking-widest text-[#F04632] hover:text-[#FF7A66] transition-colors"
              >
                Read the full refund policy →
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// FAQ
// ────────────────────────────────────────────────────────────────────────────
export function FAQ() {
  const faqs = [
    {
      q: "How fast will I actually get my Plan of Action?",
      a: "Minutes. Not hours, not days. Once you log in, connect your Amazon account, and complete the 12-question intake, you hit generate and the POA appears inside your Protego dashboard within a few minutes — as a downloadable PDF, copy-paste text, and a Google Doc link. End-to-end (checkout → POA in hand) usually runs under 10 minutes.",
    },
    {
      q: "Do I have to give you my Amazon login?",
      a: "No. We use Amazon&apos;s official <em>read-only</em> user permissions system inside Seller Central — same one accountants and PPC agencies use. We can only see performance data. We cannot list, price, message, or change anything. And if you prefer, you can skip the connection entirely and just upload the deactivation notice + relevant metrics manually. Same POA quality either way.",
    },
    {
      q: "Do you guarantee reinstatement?",
      a: "No — and nobody honest does. Amazon has the final call on every reinstatement decision, so reinstatement is not a condition of this purchase. What we do stand behind is the quality of the Plan of Action itself and one free rewrite if Amazon rejects your first submission.",
    },
    {
      q: "What if I&apos;ve already submitted a POA and it was rejected?",
      a: "This is one of the three situations we&apos;re built for. Send us your rejected POA + the Amazon response and we&apos;ll rewrite it from scratch in the correct 5-part structure, addressing a deeper root cause. Most second-submission rejections are cases we recover in one revision.",
    },
    {
      q: "Is this really written by AI? Or by a human?",
      a: "Both. Protego AI drafts the first pass using our proprietary model — trained on the 2,000+ real POA outcomes our founder has personally worked on over the last 10 years. Then Haroon personally reviews, tightens, and personalizes every single POA before it&apos;s delivered. AI does the pattern-recognition. A human — the founder himself — signs off on every case.",
    },
    {
      q: "What if my deactivation reason is really unusual (dropshipping, IP claim, safety complaint)?",
      a: "We cover every major deactivation category — Section 3, related account, inauthentic, IP infringement, safety, unfulfillable inventory, price gouging, and more. If your case is somehow outside our scope (extremely rare), we&apos;ll tell you up-front before generation begins and issue a refund under the eligibility rules in our refund policy.",
    },
    {
      q: "Why only $197?",
      a: "Because our unit economics work at $197, and because we&apos;d rather help 10,000 sellers a year than 200. About 30% of clients later hire us for ongoing account-health monitoring — that&apos;s the real business. This price is a bet that if we help you now, when you&apos;re desperate, you&apos;ll remember us when things are good.",
    },
    {
      q: "Is there any recurring charge?",
      a: "Zero. $197 flat. One time. No subscription. No &quot;premium.&quot; No trial that converts. The Stripe checkout is a single, one-off charge.",
    },
    {
      q: "What&apos;s the refund policy?",
      a: "Your Plan of Action is a custom digital work product. That means: <strong>before</strong> you submit your case details or trigger generation, you can request a full refund by emailing <a href='mailto:hello@protegoos.com' class='text-white font-semibold underline'>hello@protegoos.com</a>. <strong>Once the Plan of Action has been generated and delivered</strong>, it&apos;s non-refundable — the same way any custom-drafted document is. Amazon&apos;s reinstatement decisions are not a condition of this purchase. Full details on our <a href='/refund' class='text-white font-semibold underline'>refund policy page</a>.",
    },
  ];

  return (
    <section
      data-testid={LANDING.faq}
      className="relative py-20 md:py-32 border-t border-white/5"
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-[#F04632] font-bold mb-4">
            Quick answers
          </p>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] mb-6">
            The questions every seller asks before they hit &quot;buy.&quot;
          </h2>
        </div>

        <Accordion type="single" collapsible className="w-full space-y-3">
          {faqs.map((item, i) => (
            <AccordionItem
              key={i}
              value={`faq-${i}`}
              data-testid={LANDING.faqItem(i)}
              className="bg-[#121316] border border-white/10 rounded-xl px-5 sm:px-6 overflow-hidden hover:border-[#F04632]/30 transition-colors"
            >
              <AccordionTrigger className="text-left text-base sm:text-lg font-bold text-white hover:no-underline py-5 tracking-tight">
                <span
                  className="pr-4"
                  dangerouslySetInnerHTML={{ __html: item.q }}
                />
              </AccordionTrigger>
              <AccordionContent className="text-white/70 leading-relaxed text-base pb-5 pt-1">
                <div dangerouslySetInnerHTML={{ __html: item.a }} />
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// FINAL CTA
// ────────────────────────────────────────────────────────────────────────────
export function FinalCTA() {
  return (
    <section
      data-testid={LANDING.finalCta}
      className="relative py-24 md:py-40 border-t border-white/5 overflow-hidden"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(240,70,50,0.18),transparent_60%)]" />

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F04632]/10 border border-[#F04632]/30 text-[#FF7A66] text-xs sm:text-sm font-bold uppercase tracking-[0.18em] mb-8">
          <Rocket className="w-4 h-4" strokeWidth={2.5} />
          Last thing
        </div>

        <h2 className="text-5xl sm:text-6xl md:text-7xl font-black tracking-tighter leading-[0.9] mb-8">
          Stop refreshing Seller Central hoping the ban lifted itself.
        </h2>
        <p className="text-lg sm:text-2xl text-white/70 leading-relaxed max-w-3xl mx-auto mb-10 font-light">
          You&apos;re one Plan of Action away from a working Amazon account. The math is in
          front of you.{" "}
          <span className="text-white font-semibold">
            $197 to potentially unlock $42,458 in frozen inventory and lost daily sales.
          </span>{" "}
          That&apos;s the trade.
        </p>

        <div className="flex flex-col items-center gap-6">
          <CTAButton
            testId={LANDING.finalCtaButton}
            subtext="Delivered in your dashboard in minutes · One free revision · Secure Stripe checkout · Refund available before generation"
          >
            Get My POA Now — $197
          </CTAButton>

          <p className="text-white/50 italic text-base max-w-xl mx-auto mt-4 leading-relaxed">
            Written for you today. Submit tomorrow. Reinstated by the weekend.
          </p>

          <p className="text-3xl md:text-4xl font-black tracking-tight text-white mt-6">
            — The Protego AI team.
          </p>
        </div>
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// FOOTER
// ────────────────────────────────────────────────────────────────────────────
export function Footer() {
  return (
    <footer
      data-testid={LANDING.footer}
      className="relative py-14 border-t border-white/10 bg-black"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-[1fr_auto] items-start md:items-center justify-between gap-8 mb-10 pb-10 border-b border-white/10">
          <div>
            <p className="text-2xl font-black tracking-tighter text-white mb-1">
              Protego <span className="text-[#F04632]">AI</span>
            </p>
            <p className="text-sm text-white/50 mb-4">
              by{" "}
              <a
                href="https://section3.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/80 font-semibold hover:text-white transition-colors"
              >
                section3.ai
              </a>{" "}
              · Amazon reinstatement, engineered by Haroon Naseer.
            </p>
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs uppercase tracking-widest font-bold text-white/50">
              <a
                href="mailto:hello@protegoos.com"
                className="hover:text-white transition-colors"
              >
                Contact
              </a>
              <Link to="/terms" data-testid="footer-terms-link" className="hover:text-white transition-colors">
                Terms
              </Link>
              <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-white transition-colors">
                Privacy
              </Link>
              <Link to="/refund" data-testid="footer-refund-link" className="hover:text-white transition-colors">
                Refund policy
              </Link>
            </div>
          </div>

          {/* Social */}
          <div className="flex flex-col items-start md:items-end gap-3">
            <p className="text-xs uppercase tracking-[0.25em] text-white/40 font-bold">
              Follow Protego
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <a
                href="https://section3.ai"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="section3.ai website"
                data-testid="social-website"
                className="w-10 h-10 rounded-full bg-white/5 hover:bg-[#F04632] border border-white/10 flex items-center justify-center transition-all group"
              >
                <Globe className="w-4 h-4 text-white/70 group-hover:text-white transition-colors" strokeWidth={2.5} />
              </a>
              <a
                href="https://linkedin.com/company/protego-ai/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Protego AI on LinkedIn"
                data-testid="social-linkedin"
                className="w-10 h-10 rounded-full bg-white/5 hover:bg-[#F04632] border border-white/10 flex items-center justify-center transition-all group"
              >
                <Linkedin className="w-4 h-4 text-white/70 group-hover:text-white transition-colors" strokeWidth={2.5} />
              </a>
              <a
                href="https://www.facebook.com/protegoos"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Protego AI on Facebook"
                data-testid="social-facebook"
                className="w-10 h-10 rounded-full bg-white/5 hover:bg-[#F04632] border border-white/10 flex items-center justify-center transition-all group"
              >
                <Facebook className="w-4 h-4 text-white/70 group-hover:text-white transition-colors" strokeWidth={2.5} />
              </a>
              <a
                href="https://www.instagram.com/protegoos"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Protego AI on Instagram"
                data-testid="social-instagram"
                className="w-10 h-10 rounded-full bg-white/5 hover:bg-[#F04632] border border-white/10 flex items-center justify-center transition-all group"
              >
                <Instagram className="w-4 h-4 text-white/70 group-hover:text-white transition-colors" strokeWidth={2.5} />
              </a>
              <a
                href="https://www.tiktok.com/@section3.ai"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Section3.ai on TikTok"
                data-testid="social-tiktok"
                className="w-10 h-10 rounded-full bg-white/5 hover:bg-[#F04632] border border-white/10 flex items-center justify-center transition-all group"
              >
                {/* TikTok icon (inline SVG — lucide-react has no dedicated TikTok icon) */}
                <svg
                  viewBox="0 0 24 24"
                  className="w-4 h-4 text-white/70 group-hover:text-white transition-colors fill-current"
                  aria-hidden="true"
                >
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.72a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.15Z" />
                </svg>
              </a>
            </div>
          </div>
        </div>

        <div className="text-xs text-white/40 leading-relaxed space-y-4">
          <p>
            <strong className="text-white/60">© 2026 Section3.ai · Protego AI</strong> · All
            rights reserved. Not affiliated with, endorsed by, or sponsored by Amazon.com, Inc.
            Amazon and all related marks are trademarks of Amazon.com, Inc. or its affiliates.
          </p>
          <p>
            <strong className="text-white/60">Disclaimer:</strong> Protego AI is a
            reinstatement documentation service, not a law firm. We do not provide legal
            advice. The Plan of Action documents we produce are drafted based on publicly
            available Amazon policies and our internal research. Reinstatement decisions are
            made solely by Amazon and cannot be guaranteed by any third party. Results vary
            based on account history, deactivation reason, and Amazon&apos;s discretion.
            Historical performance data on this page is illustrative and does not constitute a
            promise of future outcome.
          </p>
          <p>
            <strong className="text-white/60">Refund policy:</strong> The Plan of Action is a
            custom digital work product. Refunds are available before your case details are
            submitted or generation is triggered — email{" "}
            <a
              href="mailto:hello@protegoos.com"
              className="font-mono text-white/70 hover:text-white transition-colors"
            >
              hello@protegoos.com
            </a>
            . Once the POA has been generated and delivered, it is non-refundable. Amazon
            reinstatement is not a condition of this purchase. Full details on our{" "}
            <Link to="/refund" className="text-white/70 hover:text-white underline transition-colors">
              refund policy page
            </Link>
            .
          </p>
        </div>
      </div>
    </footer>
  );
}
